import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Grid, Typography } from '@kc-console/common-ui';
import { useScopedLinkBuilder } from '@kc-console/context';
import { SpriteImgIcon } from '@kc-console/shared-ui';
import { IcArrowEnterRegular } from '@kep/kcds-icons';

import { useRecentSearchKeyword } from '../../../../hooks';
import ColorKeywordText from './ColorKeywordText';
import { useSearchPopover } from './SearchPopoverProvider';

import type { TokenScope } from '@kc-console/context';

interface Props {
  keyword: string;
  menu: {
    category: string;
    name: string;
    path: string;
    scope: TokenScope;
    serviceKeyword: string;
  };
  focused?: boolean;
  index?: number;
}

const SearchResultMenuItem = ({ menu, keyword, index, focused }: Props) => {
  const { name, category, serviceKeyword, path } = menu;
  const { closePopover, setFocusedIndex } = useSearchPopover();
  const { getProps } = useScopedLinkBuilder();
  const { addRecentSearchKeyword } = useRecentSearchKeyword();

  const linkProps = getProps(path, {
    onClick: () => {
      addRecentSearchKeyword(keyword);
      closePopover();
    },
  });

  // onMouseEnter 대신 onMouseMove 사용: 키보드로 스크롤 시 마우스 커서 위치에 새 요소가 들어와도 실제로 마우스가 움직이지 않았다면 포커스 변경을 방지
  const handleMouseMove = () => {
    if (index !== undefined) {
      setFocusedIndex(index);
    }
  };

  return (
    <li>
      <StyledMenuItemAnchor
        {...linkProps}
        onMouseMove={handleMouseMove}
        tabIndex={-1}
        data-search-item-index={index}
        data-focused={focused}
      >
        <MenuItemLeftContainer>
          <SpriteImgIcon fontSize={16} id={serviceKeyword} category={'BeyondServiceRegular'} />
          <StyledServiceInfo>
            <Grid container alignItems={'center'}>
              <ColorKeywordText text={name} keyword={keyword} lineHeight={'21px'} />
            </Grid>
            <ServiceDescription variant={'body/normal/xs'}>{category}</ServiceDescription>
          </StyledServiceInfo>
        </MenuItemLeftContainer>
        <div id="enter-icon">
          <IcArrowEnterRegular />
        </div>
      </StyledMenuItemAnchor>
    </li>
  );
};

const menuItemContainerStyles = css`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  cursor: pointer;
  text-decoration: none;
  color: #222;

  #enter-icon {
    display: none;
  }

  &:active {
    background-color: #f6f7f9;
  }

  &:active,
  &[data-focused='true'] {
    #enter-icon {
      display: block;
    }
  }

  &[data-focused='true'] {
    background-color: #f9fafb;
  }

  &:focus-visible {
    background-color: transparent;
    outline: 2px solid #000;
    outline-offset: -2px;
  }
`;

const StyledMenuItemAnchor = styled('a')`
  ${menuItemContainerStyles}
`;

const MenuItemLeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 4px;
`;

const ServiceDescription = styled(Typography)`
  font-size: 12px;
  color: #878e9b;
  line-height: 18px;
`;

export default SearchResultMenuItem;
